/*

👋 “Hey” from the Praxis team!

'apiConfig' is one pattern that can be used to set environment-based values at build time.
This happens by creating an environment variable REACT_APP_ENV, and then matching corresponding
keys to each environment in the 'envConfigs' object below. To learn more about how to set
REACT_APP_ENV, view the README generated with this app.


For an alternative pattern - using TAP configuration to set environment-based values at cluster initialization - see:
@praxis/component-runtime-env - https://praxis.prod.target.com/components/runtime-env

*/

import merge from 'lodash/merge'

// Base path for the Brand DNA API. This is used to construct the full URL for each endpoint.
const BRAND_DNA_API_PREFIX_PROD_V1 =
  'https://api-internal.target.com/brand_dna/v1'
const BRAND_DNA_API_PREFIX_DEV_V1 =
  'https://stgapi-internal.target.com/brand_dna/v1'
const ENTERPRISE_UPLOADS_API_PREFIX_DEV =
  'https://stage-api.target.com/enterprise_uploads/v2'
const ENTERPRISE_UPLOADS_API_PREFIX_PROD =
  'https://api.target.com/enterprise_uploads/v2'

// configurations shared between most or all environments can be stored in commonConfig
const commonConfig = {
  auth: {
    authorizationUrl:
      'https://oauth.iam.perf.target.com/auth/oauth/v2/authorize',
    logoutUrl:
      'https://logonservices.iam.perf.target.com/login/responses/logoff.html',
  },
}

const envConfigs = {
  // dev is the default development environment set by .env.development and the default pipeline in .vela.yml
  dev: {
    auth: {
      clientId: 'brand_dna_npe_im',
    },
    brandAPIs: {
      CHAT: BRAND_DNA_API_PREFIX_DEV_V1,
      USER: `${BRAND_DNA_API_PREFIX_DEV_V1}/user`,
      BRANDS: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands`,
      BRAND_CATEGORIES: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/categories`,
      BRAND_SEARCH: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/search`,
      BRAND_DOCUMENTS: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/documents`,
      DOCUMENT_TYPES: `${BRAND_DNA_API_PREFIX_DEV_V1}/document/types`,
      EXTRACTION_STATUS: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/extraction/status`,
      EXTRACTION_TRIGGER: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/extraction`,
      EXTRACTION_RETRY: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/extraction/{extractionJobId}/retry`,
      BRAND_DETAILS: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/details`,
      BRAND_PROFILE: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/profile`,
      BRAND_PUBLISH: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/publications`,
      BRAND_PUBLICATION_CURRENT: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/publications/current`,
      BRAND_PUBLICATION_RETRY: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/publications/{publicationId}/retry`,
      BRAND_PUBLICATION_CANCEL: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/publications/{publicationId}/cancel`,
      BRAND_PUBLICATION_RECOVER: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/publications/{publicationId}/recover`,
      BRAND_SECTIONS: `${BRAND_DNA_API_PREFIX_DEV_V1}/brands/{brandUuid}/sections`,
      USER_SEARCH: `${BRAND_DNA_API_PREFIX_DEV_V1}/users/search`,
      PROMPT_MODES: `${BRAND_DNA_API_PREFIX_DEV_V1}/prompt/modes`,
      PROMPT_VERSIONS: `${BRAND_DNA_API_PREFIX_DEV_V1}/prompt/versions`,
    },
    apiKey: '578015c6c752dc3be89818f27928345ac19b5d43',
    enterpriseUploads: {
      baseUrl: ENTERPRISE_UPLOADS_API_PREFIX_DEV,
      chunkConcurrency: 5,
      chunkSizeBytes: 5 * 1024 * 1024,
      simpleUploadMaxBytes: 5 * 1024 * 1024,
      tenantId: '1ac455e3-b602-4c75-b1ce-4797e186d1b0',
      uploadKey: '578015c6c752dc3be89818f27928345ac19b5d43',
    },
  },
  /*
  // additional environments can be added:
  stg: {

  },
  */
  prod: {
    auth: {
      // keys in envConfigs will overwrite keys from commonConfig
      authorizationUrl: 'https://oauth.iam.target.com/auth/oauth/v2/authorize',
      logoutUrl:
        'https://logonservices.iam.target.com/login/responses/logoff.html',
      clientId: 'brand_dna_prod_im',
    },
    apiKey: '7819ed41fa6b5cf9a5b3ea44f817e2a7df02c110',
    brandAPIs: {
      CHAT: BRAND_DNA_API_PREFIX_PROD_V1,
      USER: `${BRAND_DNA_API_PREFIX_PROD_V1}/user`,
      BRANDS: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands`,
      BRAND_CATEGORIES: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/categories`,
      BRAND_SEARCH: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/search`,
      BRAND_DOCUMENTS: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/documents`,
      DOCUMENT_TYPES: `${BRAND_DNA_API_PREFIX_PROD_V1}/document/types`,
      EXTRACTION_STATUS: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/extraction/status`,
      EXTRACTION_TRIGGER: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/extraction`,
      EXTRACTION_RETRY: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/extraction/{extractionJobId}/retry`,
      BRAND_DETAILS: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/details`,
      BRAND_PROFILE: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/profile`,
      BRAND_PUBLISH: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/publications`,
      BRAND_PUBLICATION_CURRENT: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/publications/current`,
      BRAND_PUBLICATION_RETRY: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/publications/{publicationId}/retry`,
      BRAND_PUBLICATION_CANCEL: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/publications/{publicationId}/cancel`,
      BRAND_PUBLICATION_RECOVER: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/publications/{publicationId}/recover`,
      BRAND_SECTIONS: `${BRAND_DNA_API_PREFIX_PROD_V1}/brands/{brandUuid}/sections`,
      USER_SEARCH: `${BRAND_DNA_API_PREFIX_PROD_V1}/users/search`,
      PROMPT_MODES: `${BRAND_DNA_API_PREFIX_PROD_V1}/prompt/modes`,
      PROMPT_VERSIONS: `${BRAND_DNA_API_PREFIX_PROD_V1}/prompt/versions`,
    },
    enterpriseUploads: {
      baseUrl: ENTERPRISE_UPLOADS_API_PREFIX_PROD,
      chunkConcurrency: 5,
      chunkSizeBytes: 5 * 1024 * 1024,
      simpleUploadMaxBytes: 5 * 1024 * 1024,
      tenantId: '9b505d12-6075-4abf-867f-fefa64aaa3c3',
      uploadKey: '7819ed41fa6b5cf9a5b3ea44f817e2a7df02c110',
    },
  },
}

const appEnv = process.env.REACT_APP_ENV
const config = envConfigs[appEnv]

// commonConfig and the config for the matching REACT_APP_ENV are combined.
// Values in the environment-specific config will overwrite commonConfig keys
// if they share the same name.
const apiConfig = merge(commonConfig, config)

export default apiConfig

// The following code block is purely for demonstration purposes and can be removed.
if (process.env.NODE_ENV === 'development') {
  console.log(`${appEnv} ENV apiConfig:`, apiConfig)
}
